import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticVehicleLeasingPromo } from '@//app/api/search/vehicles/[id]';
import { Checklist } from '@//components/Checklist';
import { DataList, DataListItem } from '@//components/DataList';
import { Link } from '@//components/Link';
import { TextContent } from '@//components/TextContent';
import { VStack } from '@//components/VStack';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency } from '@//utils/currency';
import { markdownToHtml } from '@//utils/text';
import { WidgetHeading } from '../../../WidgetHeading';

interface BusinessLeasingProps {
  price: number;
  leasingPromos?: ElasticVehicleLeasingPromo[] | null;
}

export const BusinessLeasing: React.FC<BusinessLeasingProps> = ({ price, leasingPromos }) => {
  const t = useTranslations('pages.item.financeSection.businessLeasing');
  const locale = useLocale();
  const marketMetadata = useMarketMetadata();
  const leasingPromo = leasingPromos?.[0];

  const checkList = useMemo(() => {
    const l: string[] = [];
    if (leasingPromo) {
      if (leasingPromo.winterTiresIncluded) {
        l.push(t('included.winterTires'));
      }
      if (leasingPromo.serviceIncluded) {
        l.push(t('included.service'));
      }
      if (leasingPromo.insuranceIncluded) {
        l.push(t('included.insurance'));
      }
    }
    return l;
  }, [leasingPromo]);

  if (!leasingPromo) {
    return (
      <VStack gap={32}>
        <WidgetHeading>{t('label')}</WidgetHeading>
        <DataList>
          <DataListItem
            label={t('monthlyCost.label')}
            value={formatCurrency(price, {
              locale,
              currency: marketMetadata.currency,
              period: 'month',
            })}
          />
        </DataList>
      </VStack>
    );
  }

  return (
    <VStack gap={32}>
      <VStack gap={16}>
        <WidgetHeading id="business-lease">{leasingPromo.title}</WidgetHeading>
        <TextContent
          textSize="md"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(leasingPromo.description) }}
        />
        <Checklist items={checkList} />
      </VStack>
      <DataList>
        <DataListItem
          label={t('monthlyCost.label')}
          value={formatCurrency(price, {
            locale,
            currency: marketMetadata.currency,
            period: 'month',
          })}
        />
        <DataListItem
          label={t('fixedTerm.label')}
          value={t('fixedTerm.value', { count: leasingPromo.periodMonths })}
        />
        <DataListItem
          label={t('yearlyDrivingRange.label')}
          value={t('yearlyDrivingRange.value', { count: leasingPromo.mileagePerYear })}
        />
      </DataList>
      <Link
        href={leasingPromo.link.url}
        title={leasingPromo.link.name}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {leasingPromo.link.name}
      </Link>
    </VStack>
  );
};
